import React from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { JsonLd, faqSchema, breadcrumbSchema } from '../components/JsonLd';
import { faqGrouped, faqFor, FAQ_META } from '../config/faqContent';
import { NAP } from '../config/nap';
import { localizedPath } from '../config/langs';
import { ui } from '../config/seoCopy';

/**
 * The FAQ page — the site's main GEO asset.
 *
 * Every answer is plain, self-contained prose under a real question heading,
 * with no accordion hiding it. Collapsed content is technically indexable but
 * answer engines consistently favour text that is present and visible in the
 * initial DOM, and the page is short enough that it does not need collapsing.
 */
export default function FaqPage() {
    const { i18n } = useTranslation();
    const lang = (i18n.language || 'ar').split('-')[0] === 'ar' ? 'ar' : 'en';
    const isRtl = lang === 'ar';
    const meta = FAQ_META[lang];
    const groups = faqGrouped(lang);

    return (
        <div style={{ minHeight: '100vh' }} dir={isRtl ? 'rtl' : 'ltr'}>
            <SEO
                fullTitle={meta.title}
                description={meta.description}
                path="/faq"
                langs={['ar', 'en']}
                keywords={
                    isRtl
                        ? 'الأسئلة الشائعة عقارات دبي, شراء على الخارطة دبي, خطط السداد دبي, بدون دفعة أولى دبي, رسوم دائرة الأراضي, الإقامة العقارية دبي'
                        : 'dubai off plan faq, buy off plan dubai, dubai payment plans, no down payment dubai, dld fees, dubai golden visa property'
                }
            />
            <JsonLd data={faqSchema(faqFor(lang), { url: `${NAP.url}${localizedPath('/faq', lang)}`, lang })} />
            <JsonLd
                data={breadcrumbSchema([
                    { name: ui(lang).home, url: '/' },
                    { name: ui(lang).faq, url: '/faq' }
                ])}
            />

            <Header />

            <main className="container" style={{ padding: '140px 1rem 5rem', maxWidth: '860px' }}>
                <Breadcrumbs items={[{ name: ui(lang).home, to: '/' }, { name: ui(lang).faq }]} />
                <h1
                    style={{
                        fontSize: 'clamp(2rem, 5vw, 3rem)',
                        fontWeight: 800,
                        color: '#fff',
                        marginBottom: '1rem',
                        lineHeight: 1.25
                    }}
                >
                    {meta.h1}
                </h1>
                <div style={{ height: '4px', width: '100px', background: 'var(--color-gold)', borderRadius: '2px', marginBottom: '2rem' }} />

                <p style={{ color: '#ccc', fontSize: '1.1rem', lineHeight: 1.9, marginBottom: '3rem' }}>
                    {meta.intro}
                </p>

                {groups.map((group) => (
                    <section key={group.id} style={{ marginBottom: '3.5rem' }}>
                        <h2
                            style={{
                                fontSize: '1.6rem',
                                fontWeight: 800,
                                color: 'var(--color-gold)',
                                marginBottom: '1.75rem',
                                paddingBottom: '0.75rem',
                                borderBottom: '1px solid rgba(255,215,0,0.25)'
                            }}
                        >
                            {group.label}
                        </h2>

                        {group.items.map((item) => (
                            <article key={item.q} style={{ marginBottom: '2rem' }}>
                                <h3
                                    style={{
                                        fontSize: '1.15rem',
                                        fontWeight: 700,
                                        color: '#fff',
                                        marginBottom: '0.6rem',
                                        lineHeight: 1.5
                                    }}
                                >
                                    {item.q}
                                </h3>
                                <p style={{ color: '#c8c8c8', lineHeight: 1.95, margin: 0, fontSize: '1.02rem' }}>
                                    {item.a}
                                </p>
                            </article>
                        ))}
                    </section>
                ))}

                {/* NAP block — the same strings as the footer and the JSON-LD,
                    stated once more in prose so a model reading only this page
                    still gets the full entity. */}
                <section
                    style={{
                        marginTop: '1rem',
                        padding: '2rem',
                        background: 'rgba(212,175,55,0.06)',
                        borderInlineStart: '3px solid var(--color-gold)',
                        borderRadius: '8px'
                    }}
                >
                    <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '1rem' }}>
                        {isRtl ? 'لم تجد إجابتك؟' : 'Still have a question?'}
                    </h2>
                    <p style={{ color: '#ccc', lineHeight: 1.9, marginBottom: '1rem' }}>
                        {isRtl
                            ? `تواصل مع ${NAP.name.ar} — وسيط عقاري مرخّص من دائرة الأراضي والأملاك بدبي برقم ${NAP.dldBrokerNumber}.`
                            : `Contact ${NAP.name.en} — a Dubai Land Department licensed broker, No. ${NAP.dldBrokerNumber}.`}
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#ccc', lineHeight: 2.1 }}>
                        <li>
                            <strong style={{ color: '#fff' }}>{isRtl ? 'الهاتف: ' : 'Phone: '}</strong>
                            <a href={`tel:${NAP.phone}`} style={{ color: 'var(--color-neon-blue)', textDecoration: 'none' }} dir="ltr">
                                {NAP.phoneDisplay}
                            </a>
                        </li>
                        <li>
                            <strong style={{ color: '#fff' }}>{isRtl ? 'البريد: ' : 'Email: '}</strong>
                            <a href={`mailto:${NAP.email}`} style={{ color: 'var(--color-neon-blue)', textDecoration: 'none' }} dir="ltr">
                                {NAP.email}
                            </a>
                        </li>
                        <li>
                            <strong style={{ color: '#fff' }}>{isRtl ? 'العنوان: ' : 'Address: '}</strong>
                            {NAP.address.full[lang]}
                        </li>
                        <li>
                            <strong style={{ color: '#fff' }}>{isRtl ? 'ساعات العمل: ' : 'Hours: '}</strong>
                            {NAP.openingHours.display[lang]}
                        </li>
                    </ul>

                    <p style={{ marginTop: '1.5rem', marginBottom: 0, display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <Link to="/projects" style={{ color: 'var(--color-gold)', fontWeight: 600, textDecoration: 'none' }}>
                            {isRtl ? 'تصفّح كل المشاريع' : 'Browse all projects'}
                        </Link>
                        <Link to="/contact" style={{ color: 'var(--color-gold)', fontWeight: 600, textDecoration: 'none' }}>
                            {isRtl ? 'احجز استشارة مجّانية' : 'Book a free consultation'}
                        </Link>
                        <Link to="/blog" style={{ color: 'var(--color-gold)', fontWeight: 600, textDecoration: 'none' }}>
                            {isRtl ? 'أدلّة الشراء' : 'Buying guides'}
                        </Link>
                    </p>
                </section>
            </main>

            <Footer />
        </div>
    );
}
